import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function DigestsTab() {
  const [digests, setDigests] = useState(null);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/compliance/digests");
    setDigests(data.digests);
    setSelected((cur) => cur || data.digests[0] || null);
  }, []);
  useEffect(() => { load().catch(() => setDigests([])); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/compliance/digests/generate");
      toast.success(`Digest ${data.period} generated`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  if (!digests) return <Skeleton className="h-72 w-full rounded-none" />;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-5" data-testid="digests-tab">
      <div className="bg-white border border-border self-start">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">Digests</p>
          <Button onClick={generate} disabled={generating} data-testid="digest-generate-btn"
            className="rounded-none h-7 text-[11px] bg-teal-700 hover:bg-teal-800 px-2.5">
            {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            Generate now
          </Button>
        </div>
        <div className="divide-y divide-border max-h-[440px] overflow-y-auto">
          {digests.map((d) => (
            <button key={d.id} onClick={() => setSelected(d)} data-testid={`digest-item-${d.id}`}
              className={`w-full text-left px-4 py-2.5 ${selected?.id === d.id ? "bg-teal-50 border-l-2 border-l-teal-600" : "hover:bg-slate-50 border-l-2 border-l-transparent"}`}>
              <p className="text-[13px] font-medium text-slate-800">{d.period}</p>
              <p className="text-[10px] font-mono text-muted-foreground">
                {d.created_at?.slice(0, 10)} · {d.trigger}
              </p>
            </button>
          ))}
          {!digests.length && <p className="px-4 py-5 text-[12px] text-muted-foreground">No digests yet.</p>}
        </div>
      </div>

      {selected ? (
        <div className="bg-white border border-border" data-testid="digest-report">
          <div className="px-6 py-4 border-b border-border flex items-center gap-3">
            <FileText className="h-4 w-4 text-teal-700" />
            <div>
              <p className="text-[15px] font-bold text-[var(--brand-primary)]">Compliance Digest — {selected.period}</p>
              <p className="text-[10px] font-mono text-muted-foreground">
                generated {selected.created_at?.slice(0, 16).replace("T", " ")} UTC · trigger: {selected.trigger}
              </p>
            </div>
            <span className="ml-auto text-[10px] font-mono px-2 py-1 bg-[var(--brand-primary)] text-white">
              score {selected.overall_score}%
            </span>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex gap-2 flex-wrap" data-testid="digest-counts">
              {[["expired", "expired", "bg-red-600 text-white"], ["expiring_30", "≤30d", "bg-amber-500 text-white"],
                ["expiring_60", "≤60d", "bg-amber-400 text-slate-900"], ["expiring_90", "≤90d", "bg-yellow-300 text-slate-900"]].map(([k, l, cls]) => (
                <span key={k} className={`text-[11px] font-mono px-2.5 py-1 ${cls}`}>{l}: {selected.counts[k]}</span>
              ))}
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-800 mb-2">Top compliance actions</p>
              <div className="space-y-1.5" data-testid="digest-actions">
                {selected.actions.map((a, i) => (
                  <div key={i} className="flex gap-2.5 items-start">
                    <span className="h-5 w-5 shrink-0 bg-[var(--brand-primary)] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <p className="text-[13px] text-slate-700 leading-snug">{a}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                  Expiring ≤ 30 days ({selected.expiring_soon.length})
                </p>
                <div className="border border-border divide-y divide-border">
                  {selected.expiring_soon.map((d, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between gap-2 text-[12px]">
                      <span className="text-slate-800">{d.employee_name}
                        <span className="text-muted-foreground ml-1.5">{d.title} · {d.department}</span></span>
                      <span className="font-mono text-amber-700 shrink-0">{d.expiry_date}</span>
                    </div>
                  ))}
                  {!selected.expiring_soon.length && <p className="px-3 py-2 text-[12px] text-muted-foreground">None.</p>}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                  Newly expired ({selected.newly_expired.length})
                </p>
                <div className="border border-border divide-y divide-border">
                  {selected.newly_expired.map((d, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between gap-2 text-[12px]">
                      <span className="text-slate-800">{d.employee_name}
                        <span className="text-muted-foreground ml-1.5">{d.title} · {d.department}</span></span>
                      <span className="font-mono text-red-600 shrink-0">{d.expiry_date}</span>
                    </div>
                  ))}
                  {!selected.newly_expired.length && <p className="px-3 py-2 text-[12px] text-muted-foreground">None since last digest.</p>}
                </div>
              </div>
            </div>

            <div data-testid="digest-spof">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                SPOF exposure — from live org diagnostics ({selected.spof_exposure.length})
              </p>
              <div className="border border-border divide-y divide-border">
                {selected.spof_exposure.map((s, i) => (
                  <div key={i} className="px-3 py-2 flex justify-between gap-2 text-[12px]">
                    <span className="text-slate-800">{s.employee_name}
                      <span className="text-muted-foreground ml-1.5">{s.title} · {s.department}</span></span>
                    <span className="font-mono text-red-600 shrink-0">{s.skills.join(", ")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-border bg-white p-8 text-[13px] text-muted-foreground">Select a digest.</div>
      )}
    </div>
  );
}
